/**
 * Read a block of data pasted from a spreadsheet back into rows and cells.
 *
 * The reliability and agreement calculators all start from the same thing: a
 * respondents × items matrix, a k × k agreement table, a subjects × categories count
 * table, or columns of category labels — copied out of Excel, Google Sheets, SPSS or a
 * CSV file. One parser serves all of them so they agree on what a header row, a missing
 * value and a decimal comma look like.
 *
 * ## Separators
 *
 * One separator per paste, chosen by the same rule `parseNumberList` uses:
 *
 * - **Tab** if the text contains one — what every spreadsheet puts on the clipboard.
 * - **Semicolon** if there is one — the CSV dialect of the locales that write a decimal
 *   comma. With either of these the comma is free to be a decimal mark, and
 *   `parseNumberInput` reads `3,5` as 3.5.
 * - **Comma** otherwise, if there is one — ordinary CSV. A comma can then never be a
 *   decimal mark, so `3,5` is two cells; the preview shows that immediately.
 * - **Spaces** when there is no other separator.
 *
 * Double-quoted cells are honoured for the three single-character separators, so a CSV
 * label such as `"Smith, J."` stays one cell.
 *
 * ## Missing values
 *
 * A blank cell, `NA`, `N/A`, `NaN`, `null`, `.`, `-` or `?` is missing. Text that is
 * none of those in a cell that should hold a number is *unreadable*: it is treated as
 * missing too, but reported with its line and column so the reader can fix it.
 */

import { parseNumberInput } from "@/lib/parse-number";

export type CellSeparator = "tab" | "semicolon" | "comma" | "space";

/** Whether the first row names the columns: guess, or the reader's explicit answer. */
export type HeaderMode = "auto" | "yes" | "no";

const LINE_BREAK = /\r\n|\r|\n/;
const WHITESPACE_RUN = /\s+/;
const MISSING_TOKEN = /^(?:|na|n\/a|nan|null|none|\.|-|–|—|\?)$/i;
const ANY_COMMA = /,/g;
const DECIMAL_COMMA = /(?<=\d),(?=\d)/g;
const SPACED_CELLS = /\S[ ]+\S/;

const SEPARATOR_CHARACTER: Record<Exclude<CellSeparator, "space">, string> = {
  tab: "\t",
  semicolon: ";",
  comma: ",",
};

export function detectSeparator(text: string): CellSeparator {
  if (text.includes("\t")) {
    return "tab";
  }
  if (text.includes(";")) {
    return "semicolon";
  }
  if (text.includes(",")) {
    return commasAreDecimalMarks(text) ? "space" : "comma";
  }
  return "space";
}

/**
 * "494,5 512" is two space-separated cells written with a decimal comma, not three
 * comma-separated ones: every comma sits between digits and spaces divide the cells.
 */
function commasAreDecimalMarks(text: string): boolean {
  const commas = text.match(ANY_COMMA)?.length ?? 0;
  const decimals = text.match(DECIMAL_COMMA)?.length ?? 0;
  return commas === decimals && SPACED_CELLS.test(text);
}

/** Split one line on a single character, keeping double-quoted cells whole. */
function splitQuoted(line: string, separator: string): string[] {
  const cells: string[] = [];
  let cell = "";
  let quoted = false;
  let index = 0;
  while (index < line.length) {
    const character = line[index];
    if (quoted && character === '"' && line[index + 1] === '"') {
      cell += '"';
      index += 2;
      continue;
    }
    if (character === '"' && (quoted || cell.trim() === "")) {
      quoted = !quoted;
      if (quoted) {
        cell = "";
      }
    } else if (character === separator && !quoted) {
      cells.push(cell.trim());
      cell = "";
    } else {
      cell += character;
    }
    index += 1;
  }
  cells.push(cell.trim());
  return cells;
}

export function splitLine(line: string, separator: CellSeparator): string[] {
  if (separator === "space") {
    const trimmed = line.trim();
    return trimmed === "" ? [] : trimmed.split(WHITESPACE_RUN);
  }
  return splitQuoted(line, SEPARATOR_CHARACTER[separator]);
}

export interface CellRows {
  separator: CellSeparator;
  rows: string[][];
  /** The 1-based line of the paste each row came from, for error messages. */
  lines: number[];
}

/** Rows of trimmed cells. Blank lines are skipped; trailing empty cells are dropped. */
export function parseCellRows(text: string): CellRows {
  const separator = detectSeparator(text);
  const rows: string[][] = [];
  const lines: number[] = [];
  for (const [index, line] of text.split(LINE_BREAK).entries()) {
    if (line.trim() === "") {
      continue;
    }
    const cells = splitLine(line, separator);
    while (cells.length > 0 && cells.at(-1) === "") {
      cells.pop();
    }
    rows.push(cells);
    lines.push(index + 1);
  }
  return { separator, rows, lines };
}

export function isMissingCell(cell: string): boolean {
  return MISSING_TOKEN.test(cell.trim());
}

/** A cell holding words rather than a number or a missing-value marker. */
export function isTextCell(cell: string): boolean {
  return !isMissingCell(cell) && parseNumberInput(cell) === null;
}

/**
 * The guess for numeric data: the first row is a header when it holds words where the
 * data holds numbers.
 *
 * Words anywhere after the first column settle it. Words in the first column alone
 * mean a header only when the rows below have numbers there — otherwise the first
 * column is a column of row labels ("Low", "Medium", "High") and the first row is data.
 */
export function looksLikeNumericHeader(rows: readonly string[][]): boolean {
  const [first, ...rest] = rows;
  if (!first || rest.length === 0) {
    return false;
  }
  if (first.slice(1).some(isTextCell)) {
    return true;
  }
  const labelsBelow = rest.every((row) => isTextCell(row[0] ?? ""));
  return isTextCell(first[0] ?? "") && !labelsBelow;
}

function resolveHeader(mode: HeaderMode, guess: () => boolean): boolean {
  if (mode === "auto") {
    return guess();
  }
  return mode === "yes";
}

export interface CellIssue {
  /** 1-based line in the paste. */
  line: number;
  /** 1-based data column. */
  column: number;
  text: string;
}

export interface NumericTable {
  separator: CellSeparator;
  /** One name per data column ("" where the header cell was blank), or null. */
  header: string[] | null;
  /** One label per data row when the first column held words, or null. */
  rowLabels: string[] | null;
  /** Data rows padded to `columns`; null marks a missing or unreadable cell. */
  rows: (number | null)[][];
  lines: number[];
  columns: number;
  missing: number;
  unreadable: CellIssue[];
}

function readNumericRow(
  cells: readonly string[],
  columns: number,
  line: number,
  table: NumericTable
): (number | null)[] {
  const values: (number | null)[] = [];
  for (let column = 0; column < columns; column++) {
    const cell = cells[column] ?? "";
    if (isMissingCell(cell)) {
      table.missing += 1;
      values.push(null);
      continue;
    }
    const value = parseNumberInput(cell);
    if (value === null) {
      table.unreadable.push({ line, column: column + 1, text: cell });
    }
    values.push(value);
  }
  return values;
}

/**
 * A numeric matrix with an optional header row and an optional column of row labels.
 *
 * Ragged rows are padded with missing cells rather than rejected, so a respondent who
 * skipped the last item reads as exactly that.
 */
export function parseNumericTable(
  text: string,
  headerMode: HeaderMode = "auto"
): NumericTable {
  const { separator, rows, lines } = parseCellRows(text);
  const hasHeader = resolveHeader(headerMode, () =>
    looksLikeNumericHeader(rows)
  );
  const headerRow = hasHeader ? (rows[0] ?? []) : null;
  const dataRows = hasHeader ? rows.slice(1) : rows;
  const dataLines = hasHeader ? lines.slice(1) : lines;
  const width = Math.max(0, ...dataRows.map((row) => row.length));
  const labelled =
    width >= 2 &&
    dataRows.length > 0 &&
    dataRows.every((row) => isTextCell(row[0] ?? ""));
  const offset = labelled ? 1 : 0;
  const columns = Math.max(0, width - offset);

  const table: NumericTable = {
    separator,
    header: null,
    rowLabels: labelled ? dataRows.map((row) => row[0]) : null,
    rows: [],
    lines: dataLines,
    columns,
    missing: 0,
    unreadable: [],
  };
  if (headerRow) {
    table.header = Array.from(
      { length: columns },
      (_, column) => headerRow[column + offset] ?? ""
    );
  }
  for (const [index, row] of dataRows.entries()) {
    table.rows.push(
      readNumericRow(row.slice(offset), columns, dataLines[index], table)
    );
  }
  return table;
}

/**
 * The guess for label data: the first row is a header when none of its words appear
 * anywhere in the rows below ("Rater A, Rater B" over "yes, no" rows).
 */
export function looksLikeLabelHeader(rows: readonly string[][]): boolean {
  const [first, ...rest] = rows;
  if (!first || rest.length < 2) {
    return false;
  }
  const seen = new Set<string>();
  for (const row of rest) {
    for (const cell of row) {
      seen.add(cell);
    }
  }
  const named = first.filter((cell) => !isMissingCell(cell));
  return named.length > 0 && named.every((cell) => !seen.has(cell));
}

export interface LabelTable {
  separator: CellSeparator;
  header: string[] | null;
  /** Rows of labels; a missing cell is "". */
  rows: string[][];
  lines: number[];
  columns: number;
}

/** Category labels — two raters' codes, or one row of ratings per subject. */
export function parseLabelTable(
  text: string,
  headerMode: HeaderMode = "auto"
): LabelTable {
  const { separator, rows, lines } = parseCellRows(text);
  const hasHeader = resolveHeader(headerMode, () => looksLikeLabelHeader(rows));
  const dataRows = hasHeader ? rows.slice(1) : rows;
  return {
    separator,
    header: hasHeader ? (rows[0] ?? []) : null,
    rows: dataRows.map((row) =>
      row.map((cell) => (isMissingCell(cell) ? "" : cell))
    ),
    lines: hasHeader ? lines.slice(1) : lines,
    columns: Math.max(0, ...dataRows.map((row) => row.length)),
  };
}

/** True when every value is a whole number — counts, not measurements. */
export function allWholeNumbers(values: readonly number[]): boolean {
  return values.every((value) => Number.isInteger(value) && value >= 0);
}
